import { lazy, Suspense, type ReactElement } from 'react';
import { createBrowserRouter } from 'react-router';
import type { UserRole } from '@seenaly/shared';
import { AppShell } from '@/components/layout/AppShell';
import { usePrefs } from '@/app/prefs';
import { useAuth } from '@/app/auth';
import { RequireAuth, RequireRole } from '@/app/guards';
import { RouteErrorBoundary } from '@/app/ErrorBoundary';
import { LoginPage } from '@/pages/auth/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

/**
 * Every screen except login is loaded on demand.
 *
 * Without this the login page downloads the entire application — barcode
 * scanner, charts, print documents — before anyone has typed a password. On a
 * clinic's Wi-Fi that is a slow first paint for no benefit. Login stays eager
 * because it is always the first thing rendered.
 */
const DashboardPage = lazy(() =>
  import('@/pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const PatientsPage = lazy(() =>
  import('@/pages/patients/PatientsPage').then((m) => ({ default: m.PatientsPage })),
);
const PatientDetailPage = lazy(() =>
  import('@/pages/patients/PatientDetailPage').then((m) => ({ default: m.PatientDetailPage })),
);
const AppointmentsPage = lazy(() =>
  import('@/pages/appointments/AppointmentsPage').then((m) => ({ default: m.AppointmentsPage })),
);
const InvoicesPage = lazy(() =>
  import('@/pages/invoices/InvoicesPage').then((m) => ({ default: m.InvoicesPage })),
);
const InventoryPage = lazy(() =>
  import('@/pages/inventory/InventoryPage').then((m) => ({ default: m.InventoryPage })),
);
const PharmacyPage = lazy(() =>
  import('@/pages/pharmacy/PharmacyPage').then((m) => ({ default: m.PharmacyPage })),
);
const LabPage = lazy(() => import('@/pages/lab/LabPage').then((m) => ({ default: m.LabPage })));
const RevenueReportPage = lazy(() =>
  import('@/pages/reports/RevenueReportPage').then((m) => ({ default: m.RevenueReportPage })),
);
const StockReportPage = lazy(() =>
  import('@/pages/reports/StockReportPage').then((m) => ({ default: m.StockReportPage })),
);
const ProfitReportPage = lazy(() =>
  import('@/pages/reports/ProfitReportPage').then((m) => ({ default: m.ProfitReportPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const StyleguidePage = lazy(() =>
  import('@/pages/styleguide/StyleguidePage').then((m) => ({ default: m.StyleguidePage })),
);
const StatusPage = lazy(() =>
  import('@/pages/status/StatusPage').then((m) => ({ default: m.StatusPage })),
);

function Shell() {
  const { t } = usePrefs();
  const { user, logout } = useAuth();
  if (!user) return null;

  return (
    <AppShell
      role={user.role}
      visibleNavKeys={user.visibleNavKeys}
      user={{
        name: user.fullName,
        roleLabel: t.roles[user.role],
        initials: initialsOf(user.fullName),
      }}
      onLogout={() => void logout()}
    />
  );
}

/** First character of the first two words — works for Lao and Latin names. */
function initialsOf(fullName: string): string {
  return fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => [...part][0] ?? '')
    .join('');
}

function Loading() {
  const { t } = usePrefs();
  return <div style={{ padding: 24, color: 'var(--sn-muted)' }}>{t.common.loading}</div>;
}

/** Wraps a lazy page in Suspense, and optionally in a role check (spec §6). */
function page(element: ReactElement, allowed?: readonly UserRole[]) {
  const suspended = <Suspense fallback={<Loading />}>{element}</Suspense>;
  return allowed ? <RequireRole allowed={allowed}>{suspended}</RequireRole> : suspended;
}

const ALL: readonly UserRole[] = ['admin', 'doctor', 'reception', 'lab'];

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteErrorBoundary /> },
  {
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: page(<DashboardPage />) },
      { path: 'status', element: page(<StatusPage />) },
      { path: 'styleguide', element: page(<StyleguidePage />) },
      { path: 'appointments', element: page(<AppointmentsPage />) },
      { path: 'patients', element: page(<PatientsPage />) },
      { path: 'patients/:id', element: page(<PatientDetailPage />) },
      { path: 'invoices', element: page(<InvoicesPage />, ['admin', 'doctor', 'reception']) },
      { path: 'inventory', element: page(<InventoryPage />, ['admin', 'doctor', 'reception']) },
      { path: 'pharmacy', element: page(<PharmacyPage />, ['admin', 'reception']) },
      { path: 'lab', element: page(<LabPage />, ALL) },
      { path: 'reports/revenue', element: page(<RevenueReportPage />, ['admin']) },
      { path: 'reports/stock', element: page(<StockReportPage />, ['admin']) },
      { path: 'reports/profit', element: page(<ProfitReportPage />, ['admin']) },
      { path: 'settings', element: page(<SettingsPage />) },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
